import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../components/AuthContext';
import { supabase } from '../lib/db';
import { useNotificationCounts } from '../lib/notifications/hooks/useNotificationCounts';
import { notificationService } from '../services/NotificationService';
import { unifiedNotificationService } from '../services/UnifiedNotificationService';
import { Capacitor } from '@capacitor/core';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'reminder' | 'update' | 'cancellation';
  status: 'unread' | 'read';
  scheduled_for: string;
  created_at: string;
  read_at: string | null;
  queue_entry_id: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { counts } = useNotificationCounts();
  
  // Calculate total unread count from all notification types
  const totalUnreadCount = counts.booking + counts.other;

  const fetchNotifications = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .in('status', ['delivered', 'read'])
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching notifications:', error);
      return;
    }

    setNotifications(data || []);
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('notifications')
      .update({ status: 'read', read_at: new Date().toISOString() })
      .eq('id', notificationId)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error marking notification as read:', error);
      return;
    }

    await fetchNotifications();
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const { error } = await supabase
      .from('notifications')
      .update({ status: 'read', read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .eq('status', 'unread');

    if (error) {
      console.error('Error marking all notifications as read:', error);
      return;
    }

    await fetchNotifications();
  };

  // Initialize notifications and permissions
  useEffect(() => {
    if (!user) {
      notificationService.stopService();
      return;
    }

    const initializeNotifications = async () => {
      try {
        console.log('[NotificationContext] Initializing notifications for user:', user.id);
        
        // Always check permissions, regardless of platform
        await unifiedNotificationService.checkAndRequestPermissions();
        
        // Initialize notification service
        const cleanup = await notificationService.initialize(user.id);

        // Initial fetch
        await fetchNotifications();

        console.log('[NotificationContext] Notifications initialized successfully');
        return cleanup;
      } catch (error) {
        console.error('[NotificationContext] Error initializing notifications:', error);
      }
    };

    const cleanup = initializeNotifications();

    // Subscribe to notification service events
    const notificationHandler = () => {
      fetchNotifications();
    };
    notificationService.addListener('notificationDelivered', notificationHandler);

    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
      notificationService.removeListener('notificationDelivered', notificationHandler);
      notificationService.stopService();
    };
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount: totalUnreadCount,
        markAsRead,
        markAllAsRead,
        refreshNotifications: fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 